import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { BasicVehicleInfo, OpenService } from '@app-services/open.service';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { DecimalPipe, PercentPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportStatus } from '@app-model/vehicle';
import { LayoutVars } from '@app/store/session/session.store';

@Component({
  selector: 'app-share-component',
  templateUrl: './share-component.component.html',
  styleUrls: ['./share-component.component.scss']
})
export class ShareComponentComponent implements OnInit {
  private readonly REDIRECT_DELAY = 1750; // 2 segundos para o prerender.io carregar
  private readonly PRERENDER_READY_TIMEOUT = 10000;
  private readonly CRAWLER_USER_AGENT = /prerender|headlesschrome|whatsapp|facebookexternalhit|twitterbot|bot|crawler|spider/i;
  private readonly AUTO_ARREMATE_NAME = 'Auto Arremate';
  private readonly AUTO_ARREMATE_LOGO = 'assets/images/logo-auto-arremate-vertical.svg';
  loading = true;
  imgUrl: string;
  vehicle: BasicVehicleInfo;
  decimalPipe = new DecimalPipe('pt-BR');
  percentPipe = new PercentPipe('pt-BR');
  private siteName: string;
  private prerenderReady = false;
  constructor(
    private route: ActivatedRoute,
    private openService: OpenService,
    private meta: Meta,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    setTimeout(() => this.setupPrerenderReady(), this.PRERENDER_READY_TIMEOUT);

    this.openService.getLayoutVars().subscribe({
      next: layoutVars => {
        this.setupPersonalization(layoutVars);
        this.loading = false;
        this.loadVehicle();
      },
      error: () => {
        this.loading = false;
        this.loadVehicle();
      }
    });
  }

  private setupPersonalization(layoutVars: LayoutVars): void {
    if (layoutVars) {
      this.imgUrl = layoutVars.logoUrl;
      this.siteName = layoutVars.name;
      MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
    } else {
      this.imgUrl = this.AUTO_ARREMATE_LOGO;
      this.siteName = this.AUTO_ARREMATE_NAME;
    }
  }

  private loadVehicle(): void {
    this.route.params.subscribe(params => {
      const vehicleId = params['id'];
      const isFastQuote = params['type'] === 'cotacao-rapida';
      this.openService.getBasicVehicleInfo(vehicleId, isFastQuote).subscribe({
        next: vehicle => {
          if (vehicle) {
            this.vehicle = vehicle;
            this.setupMetaTags(vehicle);
          } else {
            this.setupPrerenderStatusCode('404');
          }
          this.setupPrerenderReady();
          this.setupRedirect();
        },
        error: (err: HttpErrorResponse) => {
          this.setupPrerenderStatusCode(err.status === 404 ? '404' : '503');
          this.setupPrerenderReady();
          this.setupRedirect();
        }
      });
    });
  }

  private setupMetaTags(vehicle: BasicVehicleInfo): void {
    const title = `${vehicle.make} ${vehicle.trim} ${vehicle.manufactureYear}/${vehicle.modelYear}`;
    const description = this.vehicleDescription(vehicle);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: vehicle.imgUrl });
    this.meta.updateTag({ property: 'og:type', content: 'product' });
    this.meta.updateTag({ property: 'og:url', content: window.location.href });

    // Twitter Card básico
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: vehicle.imgUrl });
    this.meta.updateTag({ name: 'twitter:url', content: window.location.href });

    if (this.siteName) {
      this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
      this.meta.updateTag({ name: 'twitter:site', content: this.siteName });
      this.meta.updateTag({ name: 'twitter:creator', content: this.siteName });
    }

    // Labels e dados estruturados do Twitter Card
    this.meta.updateTag({ name: 'twitter:label1', content: 'Marca' });
    this.meta.updateTag({ name: 'twitter:data1', content: vehicle.make });
    
    this.meta.updateTag({ name: 'twitter:label2', content: 'Modelo' });
    this.meta.updateTag({ name: 'twitter:data2', content: vehicle.model });
    
    this.meta.updateTag({ name: 'twitter:label3', content: 'Versão' });
    this.meta.updateTag({ name: 'twitter:data3', content: vehicle.trim });
    
    this.meta.updateTag({ name: 'twitter:label4', content: 'Ano' });
    this.meta.updateTag({ name: 'twitter:data4', content: `${vehicle.manufactureYear}/${vehicle.modelYear}` });
    
    this.meta.updateTag({ name: 'twitter:label5', content: 'KM' });
    this.meta.updateTag({ name: 'twitter:data5', content: vehicle.km?.toLocaleString('pt-BR') });
    
    this.meta.updateTag({ name: 'twitter:label6', content: 'Preço Fipe' });
    this.meta.updateTag({ name: 'twitter:data6', content: `R$ ${vehicle.fipePrice?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` });

    //console.log favicon of html and title
  }

  private isCrawlerRequest(): boolean {
    const userAgent = window.navigator.userAgent;
    return this.CRAWLER_USER_AGENT.test(userAgent);
  }

  private setupPrerenderStatusCode(statusCode: string): void {
    this.meta.updateTag({ name: 'prerender-status-code', content: statusCode });
  }

  private setupPrerenderReady(): void {
    if (this.prerenderReady) {
      return;
    }

    this.prerenderReady = true;
    window['prerenderReady'] = true;
  }

  private setupRedirect(): void {
    // Se for uma requisição de crawler, não redireciona
    if (this.isCrawlerRequest()) {
      return;
    }

    setTimeout(() => {
      // Remove o 'share' da URL e redireciona para a página de detalhes
      const currentUrl = window.location.href;
      const newUrl = currentUrl.replace('/compartilhar/', '/');
      window.location.href = newUrl;
    }, this.REDIRECT_DELAY);
  }

  private vehicleDescription(vehicle: BasicVehicleInfo): string {
    let message = `
            ${vehicle.oemDealerName}

🛣️ Quilometragem: ${this.decimalPipe.transform(vehicle.km)}
🏙️ Cidade: ${vehicle.city} / ${vehicle.uf}`;

if(vehicle.fipePrice){
  message = message.concat(`
💰 FIPE: R$${this.decimalPipe.transform(vehicle.fipePrice)}
`)
}

if(vehicle.price){
  message = message.concat(`
💰 Oferta Inicial: R$${this.decimalPipe.transform(vehicle.price)}`)
}

if(vehicle.reportStatus){
  message = message.concat(`
📋 Laudo: ${vehicle.reportStatus ? ReportStatus[vehicle.reportStatus] : 'N/A'}`)
}


    return message;
  }
}
